import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AnalysisSummary as AnalysisItem, fetchAnalysis, updateAnalysisReview } from '../../../services/api'
import { getStoredEmail, clearStoredEmail, getStoredMode, getStoredTeamName, clearTeamSettings, getStoredModules, setStoredModules, ReviewMode } from '../../../services/user'
import { useTheme } from '../../layout/ThemeProvider'
import { Filters, DEFAULT_FILTERS, View, matchesView, withinDateRange, sortItems, daysUntil } from './helpers'
import { AnalysisSkeleton } from './Skeleton'
import { StatusStrip } from './StatusStrip'
import { FilterBar } from './FilterBar'
import { AnalysisCard, CompactRow } from './AnalysisCard'
import { DetailPage } from './DetailPage'
import { EmailGate } from './EmailGate'
import { ModeGate } from './ModeGate'

function Empty({ onClear, onViewAll, view, padded }: { onClear: () => void; onViewAll: () => void; view: View; padded?: boolean }) {
  const allClear = view === 'attention'
  return (
    <div className={`text-center text-an-ink-3 text-[13px] ${padded ? 'py-14' : 'py-10'}`}>
      {allClear ? 'Nothing needs action right now. ' : 'No items match. '}
      <button
        onClick={allClear ? onViewAll : onClear}
        className="text-an-accent bg-transparent border-none cursor-pointer text-[13px]"
      >
        {allClear ? 'View all' : 'Clear filters'}
      </button>
    </div>
  )
}

const Analysis: React.FC = () => {
  const { id: paramId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { theme, toggleTheme } = useTheme()
  const [items, setItems] = useState<AnalysisItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [f, setF] = useState<Filters>(() => ({ ...DEFAULT_FILTERS, modules: getStoredModules() }))
  const [compact, setCompact] = useState(() => localStorage.getItem('lt_analysis_compact') === '1')
  const [userEmail, setUserEmail] = useState<string | null>(() => getStoredEmail())
  const [mode, setMode] = useState<ReviewMode>(() => getStoredMode())
  const [teamName, setTeamName] = useState<string | null>(() => getStoredTeamName())
  const [modeChosen, setModeChosen] = useState(() => localStorage.getItem('lt_mode') !== null)
  const needsMode = userEmail !== null && !modeChosen

  const selectedId = paramId ? parseInt(paramId, 10) : null

  useEffect(() => {
    if (!userEmail || needsMode) {
      setItems([])
      setLoading(false)
      return
    }
    setLoading(true)
    const tm = mode === 'team' ? teamName : undefined
    fetchAnalysis(userEmail, tm || undefined)
      .then(setItems)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [userEmail, mode, teamName, needsMode])

  useEffect(() => { localStorage.setItem('lt_analysis_compact', compact ? '1' : '0') }, [compact])
  useEffect(() => { setStoredModules(f.modules) }, [f.modules])

  function goToDetail(id: number) { navigate(`/analysis/${id}`) }
  function goBack() { navigate('/') }
  function handleChangeSettings() {
    clearStoredEmail()
    clearTeamSettings()
    setUserEmail(null)
    setMode('individual')
    setTeamName(null)
    setModeChosen(false)
  }

  if (selectedId !== null) {
    if (!userEmail) {
      return (
        <div className="flex-1 overflow-y-auto min-w-0 bg-an-bg animate-[an-fadeIn_0.18s_ease] font-geist">
          <div className="max-w-[860px] mx-auto px-6 pt-7 pb-16 flex flex-col gap-5">
            <EmailGate onSubmit={email => setUserEmail(email)} />
          </div>
        </div>
      )
    }
    if (needsMode) {
      return (
        <div className="flex-1 overflow-y-auto min-w-0 bg-an-bg animate-[an-fadeIn_0.18s_ease] font-geist">
          <div className="max-w-[860px] mx-auto px-6 pt-7 pb-16 flex flex-col gap-5">
            <ModeGate userEmail={userEmail} onSubmit={(m, tn) => { setMode(m); setTeamName(tn); setModeChosen(true); setLoading(true) }} />
          </div>
        </div>
      )
    }
    if (loading) {
      return (
        <div className="flex-1 overflow-y-auto min-w-0 bg-an-bg animate-[an-fadeIn_0.18s_ease] font-geist">
          <div className="max-w-[860px] mx-auto px-6 pt-7 pb-16 flex flex-col gap-5">
            <AnalysisSkeleton />
          </div>
        </div>
      )
    }
    return (
      <DetailPage
        id={selectedId}
        items={items}
        setItems={setItems}
        onBack={goBack}
        animDir="forward"
        userEmail={userEmail}
        teamName={mode === 'team' ? teamName : undefined}
        onNeedEmail={() => setUserEmail(null)}
      />
    )
  }

  const countries = Array.from(new Set(items.map(i => i.country ?? 'EU'))).sort()
  const allModules = Array.from(new Set(items.flatMap(i => (i.workday_modules ?? []).map(m => m.module)))).sort()

  const relevantItems = items.filter(i => i.is_relevant !== false)
  const notRelevantCount = items.length - relevantItems.length
  const counts: Record<View, number> = {
    attention: relevantItems.filter(i => matchesView(i, 'attention')).length,
    informational: relevantItems.filter(i => matchesView(i, 'informational')).length,
    reviewed: relevantItems.filter(i => matchesView(i, 'reviewed')).length,
    all: relevantItems.length + (f.showNotRelevant ? notRelevantCount : 0),
  }

  const filtered = items.filter(i => {
    if (i.is_relevant === false) {
      if (!f.showNotRelevant) return false
    } else {
      if (!matchesView(i, f.view)) return false
    }
    if (f.country !== 'all' && (i.country ?? 'EU') !== f.country) return false
    if (f.crit !== 'all' && i.criticality !== f.crit) return false
    if (f.modules.length > 0) {
      const itemModules = (i.workday_modules ?? []).map(m => m.module)
      if (!f.modules.some(fm => itemModules.includes(fm))) return false
    }
    if (f.q) {
      const q = f.q.toLowerCase()
      if (!i.title.toLowerCase().includes(q) && !i.one_liner.toLowerCase().includes(q)) return false
    }
    if (!withinDateRange(i.analysed_at, f.dateRange)) return false
    if (f.hidePastDeadline && (i.deadline_passed === true || (i.compliance_deadline != null && i.compliance_deadline !== '' && daysUntil(i.compliance_deadline) < 0))) return false
    return true
  }).sort((a, b) => sortItems(a, b, f.sortBy))

  function handleQuickStatus(id: number, status: string) {
    if (!userEmail) return
    const tm = mode === 'team' ? teamName || undefined : undefined
    updateAnalysisReview(id, status, userEmail, tm)
      .then(updated => setItems(prev => prev.map(i => i.id === id ? { ...i, review_status: updated.review_status, reviewed_by: updated.reviewed_by } : i)))
      .catch(() => {})
  }

  const hasFilters = !!f.q || f.crit !== 'all' || f.view !== DEFAULT_FILTERS.view || f.country !== 'all' || f.modules.length > 0 || f.showNotRelevant || f.dateRange !== 'all' || f.sortBy !== 'urgency' || !f.hidePastDeadline

  return (
    <div className="flex-1 overflow-y-auto min-w-0 bg-an-bg min-h-screen animate-[an-fadeIn_0.18s_ease] font-geist">
      <div className="max-w-[860px] mx-auto px-6 pt-5 pb-16 flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-[18px] h-[18px] rounded-[5px] bg-an-accent text-white text-[8.5px] font-extrabold leading-none">LT</span>
            <span className="text-[12px] font-semibold text-an-ink tracking-[-0.02em]">Legislation Tracker</span>
          </div>
          <div className="flex items-center gap-2.5 text-[11.5px]">
            {userEmail && (
              <>
                <div className="flex items-center gap-1.5 text-an-ink-3">
                  <span className="text-an-ink-2 font-medium">{userEmail.split('@')[0]}</span>
                  {mode === 'team' && teamName && (
                    <><span className="opacity-30">·</span><span>{teamName}</span></>
                  )}
                  {mode === 'individual' && !needsMode && (
                    <><span className="opacity-30">·</span><span>Individual</span></>
                  )}
                  <span className="opacity-30">·</span>
                  <button onClick={handleChangeSettings} className="text-an-accent bg-transparent border-none cursor-pointer text-[11.5px] p-0 font-medium">Change</button>
                </div>
                <div className="w-px h-3 bg-an-line-mid" />
              </>
            )}
            <Link to="/legislation" className="text-[11.5px] text-an-ink-3 font-medium no-underline hover:text-an-ink-2 transition-colors">
              Legislation
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="inline ml-0.5 -mt-px">
                <path d="M3 2l4 3-4 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <div className="w-px h-3 bg-an-line-mid" />
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center w-6 h-6 rounded text-an-ink-3 hover:text-an-ink-2 hover:bg-an-surface-2 transition-colors cursor-pointer bg-transparent border-none"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="3" stroke="currentColor" strokeWidth="1.3" />
                  <path d="M7 1v1.5M7 11.5V13M1 7h1.5M11.5 7H13M2.76 2.76l1.06 1.06M10.18 10.18l1.06 1.06M11.24 2.76l-1.06 1.06M3.82 10.18l-1.06 1.06" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path d="M12.5 8.5a5.5 5.5 0 01-7-7A5.5 5.5 0 1012.5 8.5z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {!userEmail && (
          <EmailGate onSubmit={email => setUserEmail(email)} />
        )}

        {needsMode && (
          <ModeGate
            userEmail={userEmail!}
            onSubmit={(m, tn) => { setMode(m); setTeamName(tn); setModeChosen(true) }}
          />
        )}

        {loading && !needsMode && <AnalysisSkeleton />}
        {error && <p className="text-[13px] text-an-red">Failed to load: {error}</p>}

        {!loading && !error && !needsMode && (
          <>
            <StatusStrip items={items} onSelectView={v => setF(x => ({ ...x, view: v }))} />
            <FilterBar f={f} setF={setF} compact={compact} setCompact={setCompact} countries={countries} counts={counts} modules={allModules} />

            {compact ? (
              <div className="rounded-card overflow-hidden border border-an-line bg-an-surface">
                {filtered.length === 0
                  ? <Empty onClear={() => setF(x => ({ ...DEFAULT_FILTERS, modules: x.modules }))} onViewAll={() => setF(x => ({ ...x, view: 'all' }))} view={f.view} />
                  : filtered.map(i => <CompactRow key={i.id} item={i} onClick={goToDetail} />)
                }
              </div>
            ) : (
              <div className="flex flex-col gap-[7px]">
                {filtered.length === 0
                  ? <Empty onClear={() => setF(x => ({ ...DEFAULT_FILTERS, modules: x.modules }))} onViewAll={() => setF(x => ({ ...x, view: 'all' }))} view={f.view} padded />
                  : filtered.map(i => <AnalysisCard key={i.id} item={i} onClick={goToDetail} onQuickStatus={handleQuickStatus} />)
                }
              </div>
            )}

            {hasFilters && filtered.length > 0 && (
              <div className="text-center text-xs text-an-ink-3">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Analysis
